import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import moment from "moment";
import "moment/locale/ar";
import "moment/locale/hi";
import "moment/locale/en-gb";
import PublicSharpIcon from "@mui/icons-material/PublicSharp";
import GroupSharpIcon from "@mui/icons-material/GroupSharp";
import LockSharpIcon from "@mui/icons-material/LockSharp";
import TuneSharpIcon from "@mui/icons-material/TuneSharp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import ReplyAllOutlinedIcon from "@mui/icons-material/ReplyAllOutlined";
import axios from "../../API/axios";
import { getEmojiDDL } from "../../Services/apiService";
import { getLangKey } from "../../utils/language";
import { post, people } from "../../config/imagesLink";
import { ImageWithLoader, PostFeedSkeleton } from "@skeleton";
import {
  resolveFeedPostPinnedState,
  toNewsFeedItemFromGetPostInfo,
} from "../../utility/newsFeedPost";
import PostComponent from "../NewsFeed/PostComponent";
import PostInteractionModal from "../NewsFeed/PostInteractionModal";
import PostComment from "../NewsFeed/PostComment";
import PostEngagement from "../NewsFeed/PostEngagement";
import Comment from "../NewsFeed/Comment";
import ImageSliderModal from "../NewsFeed/ImageSliderModal";
import LikesDetails from "../NewsFeed/LikesDetails";
import PostVideoCommon from "../PersonalProfile/PostVideoCommon";

const localeMap = { en: "en", ar: "ar", hi: "hi" };

const getMediaCellPadding = (mediaCount, idx) => {
  if (mediaCount === 3 && idx === 0) return "0 0 5px 0";
  if (mediaCount === 3 && idx === 1) return "0 5px 5px 0";
  if (mediaCount === 4 && idx === 2) return "0 5px 0 0";
  if (mediaCount === 4 && idx === 0) return "0 5px 5px 0";
  if (mediaCount === 2 && idx === 0) return "0 5px 5px 0";
  return "";
};

const getFeedRelativeTime = (creationTime, t) => {
  const now = moment.utc().local();
  const itemTime = moment.utc(creationTime).local();
  const diffSeconds = now.diff(itemTime, "seconds");
  const diffMinutes = now.diff(itemTime, "minutes");
  const diffHours = now.diff(itemTime, "hours");
  const diffDays = now.diff(itemTime, "days");
  const diffMonths = now.diff(itemTime, "months", true);
  const diffYears = now.diff(itemTime, "years", true);

  if (diffSeconds < 60) return t("Notifications.Just Now");
  if (diffMinutes < 60) {
    return diffMinutes === 1
      ? `${diffMinutes} ${t("Notifications.Minute")} ${t("Notifications.Ago")}`
      : `${diffMinutes} ${t("Notifications.Minutes")} ${t("Notifications.Ago")}`;
  }
  if (diffHours < 24) {
    return diffHours === 1
      ? `${diffHours} ${t("Notifications.Hour")} ${t("Notifications.Ago")}`
      : `${diffHours} ${t("Notifications.Hours")} ${t("Notifications.Ago")}`;
  }
  if (diffDays < 30) {
    return diffDays === 1
      ? `${diffDays} ${t("Notifications.Day")} ${t("Notifications.Ago")}`
      : `${diffDays} ${t("Notifications.Days")} ${t("Notifications.Ago")}`;
  }
  if (diffMonths < 12) {
    const displayMonth =
      diffMonths >= 0.75 && diffMonths < 1.25
        ? 1
        : Math.round(diffMonths * 2) / 2;
    return displayMonth === 1
      ? `${t("Notifications.PostedAround")} ${displayMonth} ${t("Notifications.Month")} ${t("Notifications.Ago")}`
      : `${t("Notifications.PostedAround")} ${displayMonth} ${t("Notifications.Months")} ${t("Notifications.Ago")}`;
  }
  const displayYear =
    diffYears >= 0.75 && diffYears < 1.25
      ? 1
      : Math.round(diffYears * 2) / 2;
  return displayYear === 1
    ? `${t("Notifications.PostedAround")} ${displayYear} ${t("Notifications.Year")} ${t("Notifications.Ago")}`
    : `${t("Notifications.PostedAround")} ${displayYear} ${t("Notifications.Years")} ${t("Notifications.Ago")}`;
};

const SearchNewsFeedPosts = ({
  posts = [],
  setPosts,
  page,
  setPage,
  totalPages,
  setTotalPages,
  renderFallback,
}) => {
  const { t } = useTranslation();
  const guestModeData = localStorage.getItem("guestMode");
  const userUid = localStorage.getItem("userUid");
  const restrictedViewerToken = localStorage.getItem("restrictedViewerToken");
  const language = localStorage.getItem("Lang");
  const locale = localeMap[language] || "en";
  const visibleVideoRef = useRef(null);

  const [emojiList, setEmojiList] = useState();
  const [postLikeState, setPostLikeState] = useState({});
  const [isfollowed, setIsfollowed] = useState(false);
  const [isfollowedComp, setIsfollowedComp] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [showDeletePost, setShowDeletePost] = useState(false);
  const [dLike] = useState(0);
  const [singlepostId, setSinglePostId] = useState("");
  const [singlePostContent, setSinglePostContent] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ispostmodalOpen, setIspostmodalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [imageArray, setImageArray] = useState([]);
  const [initialSlideIndex, setInitialSlideIndex] = useState(0);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [postLikedType, setPostLikedType] = useState(false);
  const [likesPostId, setLikesPostId] = useState("");
  const [modalLoading, setModalLoading] = useState(false);

  const setPostList = useCallback(
    (updater) => {
      setPosts((prev) => {
        const unenriched = [];
        const enriched = [];
        prev.forEach((item) => {
          if (item?.feedItem?.postInfo?.postId) enriched.push(item);
          else unenriched.push(item);
        });

        const feedList = enriched.map((item) => item.feedItem);
        const nextFeed =
          typeof updater === "function" ? updater(feedList) : updater;
        if (!Array.isArray(nextFeed)) return prev;

        const merged = nextFeed
          .map((feedItem) => {
            const postId = feedItem?.postInfo?.postId;
            if (postId == null) return null;
            const existing = enriched.find(
              (item) =>
                String(item.feedItem.postInfo.postId) === String(postId)
            );
            if (existing) return { ...existing, feedItem };
            return {
              id: postId,
              type: "Post",
              enriched: true,
              feedItem,
            };
          })
          .filter(Boolean);

        return [...merged, ...unenriched];
      });
    },
    [setPosts]
  );

  const postList = useMemo(
    () => posts.map((item) => item.feedItem).filter((item) => item?.postInfo?.postId),
    [posts]
  );

  const AllPostList = useCallback(async () => {}, []);

  useEffect(() => {
    let cancelled = false;
    const loadEmojis = async () => {
      try {
        const responseData = await getEmojiDDL(language);
        if (!cancelled) setEmojiList(responseData);
      } catch (error) {
        console.error(error);
      }
    };
    loadEmojis();
    return () => {
      cancelled = true;
    };
  }, [language]);

  const handleProfileImageError = (event) => {
    if (!event?.target) return;
    event.target.onerror = null;
    event.target.src = people.DefaultProfile;
  };

  const handlePostMediaImageError = (event) => {
    if (!event?.target) return;
    event.target.onerror = null;
    event.target.src = post.noimgavailable;
  };

  const updateLikeCount = (postId, delta, isLikedStatus, likedType) => {
    setPostList((prev) =>
      prev.map((post) =>
        String(post?.postInfo?.postId) === String(postId)
          ? {
              ...post,
              postInfo: {
                ...post.postInfo,
                likeCount: Math.max((post.postInfo.likeCount || 0) + delta, 0),
                postActionsInfo: {
                  ...post.postInfo.postActionsInfo,
                  ...(typeof isLikedStatus === "boolean"
                    ? { isLiked: isLikedStatus }
                    : {}),
                  ...(likedType !== undefined ? { likedType } : {}),
                },
              },
            }
          : post
      )
    );
    if (typeof isLikedStatus === "boolean") {
      setPostLikeState((prev) => ({ ...prev, [String(postId)]: isLikedStatus }));
    }
  };

  const updatePostLike = (postId, delta, isLikedStatus, likedType) => {
    setPostList((prev) =>
      prev.map((post) =>
        String(post?.postInfo?.postId) === String(postId)
          ? {
              ...post,
              postInfo: {
                ...post.postInfo,
                postActionsInfo: {
                  ...post.postInfo.postActionsInfo,
                  isLiked: isLikedStatus,
                  likedType,
                },
              },
            }
          : post
      )
    );
    if (typeof isLikedStatus === "boolean") {
      setPostLikeState((prev) => ({ ...prev, [String(postId)]: isLikedStatus }));
    }
  };

  const updateCommentCount = (postId, change) => {
    setPostList((prev) =>
      prev.map((post) =>
        String(post?.postInfo?.postId) === String(postId)
          ? {
              ...post,
              postInfo: {
                ...post.postInfo,
                commentCount: Math.max(
                  (post.postInfo?.commentCount || 0) + change,
                  0
                ),
              },
            }
          : post
      )
    );
  };

  const updateShareCount = (postId, change) => {
    setPostList((prev) =>
      prev.map((post) =>
        String(post?.postInfo?.postId) === String(postId)
          ? {
              ...post,
              postInfo: {
                ...post.postInfo,
                shareCount: Math.max((post.postInfo?.shareCount || 0) + change, 0),
              },
            }
          : post
      )
    );
  };

  const updateCommentLike = (postId, commentId, action, likeType = null) => {
    setPostList((prevList) =>
      prevList.map((item) => {
        if (String(item?.postInfo?.postId) !== String(postId)) return item;
        return {
          ...item,
          postInfo: {
            ...item.postInfo,
            comments: (item.postInfo.comments || []).map((comment) => {
              const commentKey = comment?.pstCmntId ?? comment?.commentId;
              if (String(commentKey) !== String(commentId)) return comment;
              if (action === "like") {
                return {
                  ...comment,
                  likeCount: (comment.likeCount || 0) + 1,
                  isLiked: true,
                  likedType: likeType,
                };
              }
              if (action === "unlike") {
                return {
                  ...comment,
                  likeCount: Math.max((comment.likeCount || 1) - 1, 0),
                  isLiked: false,
                  likedType: null,
                };
              }
              if (action === "change") {
                return { ...comment, isLiked: true, likedType: likeType };
              }
              return comment;
            }),
          },
        };
      })
    );
  };

  const updateReplyCommentLike = (
    postId,
    commentId,
    replyId,
    action,
    likeType = null
  ) => {
    setPostList((prevList) =>
      prevList.map((postItem) => {
        if (String(postItem?.postInfo?.postId) !== String(postId)) return postItem;
        return {
          ...postItem,
          postInfo: {
            ...postItem.postInfo,
            comments: (postItem.postInfo.comments || []).map((comment) => {
              if (String(comment.pstCmntId) !== String(commentId)) return comment;
              return {
                ...comment,
                commentsReplies: (comment.commentsReplies || []).map((reply) => {
                  if (String(reply.pstCmntRplyId) !== String(replyId)) return reply;
                  if (action === "like") {
                    return {
                      ...reply,
                      isLiked: true,
                      likeCount: (reply.likeCount || 0) + 1,
                      likedType: likeType,
                    };
                  }
                  if (action === "unlike") {
                    return {
                      ...reply,
                      isLiked: false,
                      likeCount: reply.likeCount > 0 ? reply.likeCount - 1 : 0,
                      likedType: null,
                    };
                  }
                  if (action === "change") {
                    return { ...reply, isLiked: true, likedType: likeType };
                  }
                  return reply;
                }),
              };
            }),
          },
        };
      })
    );
  };

  const syncPostInFeed = useCallback(
    async (postIdToSync) => {
      if (!postIdToSync) return;
      try {
        const response = await axios.get(
          `/Posts/GetPostInfo?DeviceType=Web&PostId=${postIdToSync}&RestrictedViewerToken=${restrictedViewerToken || ""}&langKey=${getLangKey(language)}`
        );
        const feedItem = toNewsFeedItemFromGetPostInfo(
          response?.data?.responseData
        );
        if (!feedItem) return;
        if (typeof feedItem.postInfo?.postActionsInfo?.isLiked === "boolean") {
          setPostLikeState((prev) => ({
            ...prev,
            [String(postIdToSync)]: feedItem.postInfo.postActionsInfo.isLiked,
          }));
        }
        setPostList((prevList) =>
          prevList.map((postItem) =>
            String(postItem?.postInfo?.postId) === String(postIdToSync)
              ? {
                  ...postItem,
                  ...feedItem,
                  postInfo: {
                    ...postItem.postInfo,
                    ...feedItem.postInfo,
                    comments:
                      feedItem.postInfo?.comments ||
                      postItem.postInfo?.comments ||
                      [],
                  },
                }
              : postItem
          )
        );
      } catch (error) {
        console.error("Failed to sync search post after modal close:", error);
      }
    },
    [language, restrictedViewerToken, setPostList]
  );

  const openModal = (images, initialSlide = 0) => {
    const files = Array.isArray(images) ? images : [];
    setModalImages(files);
    setImageArray(
      files.map((file) =>
        typeof file === "string" ? { mediaName: file } : file
      )
    );
    setInitialSlideIndex(
      typeof initialSlide === "number" && initialSlide >= 0 ? initialSlide : 0
    );
    setModalLoading(true);
    setIsModalOpen(true);
  };

  const closeModal = (closePayload = singlepostId) => {
    const isPayloadObject =
      typeof closePayload === "object" && closePayload !== null;
    const postIdToSync = isPayloadObject
      ? closePayload?.postId ?? singlepostId
      : closePayload;
    setIsModalOpen(false);
    setIspostmodalOpen(false);
    if (postIdToSync) {
      setTimeout(() => syncPostInFeed(postIdToSync), 350);
    }
  };

  const handleMediaClick = (item, idx) => {
    const mediaList = item?.postInfo?.mediaFilesList || [];
    const imageData = mediaList.map((file) => file.mediaName);
    setSinglePostContent(item);
    setSinglePostId(item?.postInfo?.postId);
    setImageArray(mediaList);
    setInitialSlideIndex(idx);

    if (
      item?.cntntTypCategory === "UserStory" ||
      item?.cntntTypCategory === "Text" ||
      item?.cntntTypCategory === "TextWithImage" ||
      item?.cntntTypCategory === "Image"
    ) {
      openModal(imageData, idx);
    }
    if (
      item?.cntntTypCategory === "Article" ||
      item?.contentType === "ArticlePost"
    ) {
      window.open(
        `/Article/${item?.postInfo?.postId}/${item?.postInfo?.articleInfo?.titleSlug}`,
        "_blank"
      );
    }
  };

  const currentModalPost = postList.find(
    (postItem) => String(postItem?.postInfo?.postId) === String(singlepostId)
  );
  const modalIsLiked = Boolean(
    postLikeState[String(singlepostId)] ??
      currentModalPost?.postInfo?.postActionsInfo?.isLiked
  );

  const handleModalSetIsLiked = (likedValue) => {
    const modalPostId = singlepostId || currentModalPost?.postInfo?.postId;
    if (!modalPostId) return;
    const nextLiked = Boolean(likedValue);
    setPostLikeState((prev) => ({ ...prev, [String(modalPostId)]: nextLiked }));
    setPostList((prevList) =>
      prevList.map((postItem) =>
        String(postItem?.postInfo?.postId) === String(modalPostId)
          ? {
              ...postItem,
              postInfo: {
                ...postItem.postInfo,
                postActionsInfo: {
                  ...postItem?.postInfo?.postActionsInfo,
                  isLiked: nextLiked,
                },
              },
            }
          : postItem
      )
    );
  };

  const handleShowLikes = (item) => {
    setLikesPostId(item?.postInfo?.postId);
    setPostLikedType(item?.postInfo?.postActionsInfo?.likedType);
    setShowLikesModal(true);
  };

  const renderFeedCard = (item) => {
    const mediaList = item?.postInfo?.mediaFilesList || [];
    const mediaCount = mediaList.length;
    const isArticle =
      item.cntntTypCategory === "Article" || item.contentType === "ArticlePost";
    const isVideo =
      item?.cntntTypCategory === "TextWithVideo" ||
      item?.cntntTypCategory === "Video";
    const relativeTime = getFeedRelativeTime(item?.postInfo?.creationTime, t);
    const absoluteTime = moment
      .utc(item?.postInfo?.creationTime)
      .locale(locale)
      .local()
      .format(
        language === "ar"
          ? "D MMMM YYYY [الساعة] h:mm a"
          : language === "en"
            ? "D MMMM YYYY [at] h:mm a"
            : "D MMMM YYYY [को] h:mm a"
      );
    const profileLink =
      item?.postInfo?.postOriginType === "Company"
        ? `/Company/${item?.postInfo?.userUid}/${item?.postInfo?.userName}`
        : `/User/${item?.postInfo?.userUid}`;

    return (
      <div
        className="featured-card user-postOnlyContent p-2"
        key={item?.postInfo?.postId}
      >
        <div className="feature-content p-md-2 d-flex justify-content-between mb-0 RemovePaddingMobile">
          <Link className="mb-1" to={profileLink}>
            <div className="d-flex">
              <div className="ImgAvatar">
                <img
                  src={item?.postInfo?.userAvatar || people.DefaultProfile}
                  alt=""
                  style={{ borderRadius: "50px" }}
                  onError={handleProfileImageError}
                />
              </div>
              <div className="ms-2 mb-2">
                <div className="d-flex userName align-items-center gap-1 flex-wrap">
                  <h4 className="f-14 fw-bold mb-0" dir="ltr">
                    {item?.postInfo?.userName}
                  </h4>
                  {item?.postInfo?.nameTag ? (
                    <span
                      className="f-12 fw-600"
                      dir="ltr"
                      style={{ color: "rgb(58, 116, 246)" }}
                    >
                      {item.postInfo.nameTag}
                    </span>
                  ) : null}
                </div>
                {item?.postInfo?.title ? (
                  <p className="f-12 mb-0">{item.postInfo.title}</p>
                ) : null}
                {item?.postInfo?.additionalInfo ? (
                  <p className="f-12 mb-0">{item.postInfo.additionalInfo}</p>
                ) : null}
                <div className="d-flex justify-content-start align-items-center">
                  <Tooltip title={absoluteTime} arrow>
                    <p className="f-10 label mb-0">{relativeTime}</p>
                  </Tooltip>
                  <div className="ms-1 d-flex align-items-center">
                    {item?.postInfo?.privacyType === "Public" && (
                      <>
                        <span className="f-800 me-1 ms-1">·</span>
                        <Tooltip title={t("Newsfeed.Public")} arrow>
                          <PublicSharpIcon
                            fontSize="inherit"
                            className="label"
                            style={{
                              color: "#9e9e9e",
                              fontSize: "12px",
                              display: "block",
                            }}
                          />
                        </Tooltip>
                      </>
                    )}
                    {item?.postInfo?.privacyType === "Friends" && (
                      <>
                        <span className="f-800 me-1 ms-1">·</span>
                        <Tooltip title={t("Newsfeed.Friends")} arrow>
                          <GroupSharpIcon
                            fontSize="inherit"
                            className="label"
                            style={{
                              color: "#9e9e9e",
                              fontSize: "12px",
                              display: "block",
                            }}
                          />
                        </Tooltip>
                      </>
                    )}
                    {item?.postInfo?.privacyType === "OnlyMe" && (
                      <>
                        <span className="f-800 me-1 ms-1">·</span>
                        <Tooltip title={t("Newsfeed.OnlyMe")} arrow>
                          <LockSharpIcon
                            fontSize="inherit"
                            className="label"
                            style={{
                              color: "#9e9e9e",
                              fontSize: "12px",
                              display: "block",
                            }}
                          />
                        </Tooltip>
                      </>
                    )}
                    {item?.postInfo?.privacyType === "Custom" && (
                      <>
                        <span className="f-800 me-1 ms-1">·</span>
                        <Tooltip title={t("Newsfeed.Custom")} arrow>
                          <TuneSharpIcon
                            fontSize="inherit"
                            className="label"
                            style={{
                              color: "#9e9e9e",
                              fontSize: "12px",
                              display: "block",
                            }}
                          />
                        </Tooltip>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Link>

          {guestModeData === "true" ? null : (
            <div className="d-flex align-items-center wlj-feed-post-actions">
              {item.cntntTypCategory === "SharePost" ? (
                <span className="wlj-feed-type-badge-wrap">
                  <span className="badge badge-subtle badge-info1 f-12 wlj-feed-type-badge">
                    {t("Newsfeed.Shared Post")}
                  </span>
                </span>
              ) : item.cntntTypCategory === "Article" ? (
                <span className="wlj-feed-type-badge-wrap">
                  <span className="badge badge-subtle badge-info1 f-12 wlj-feed-type-badge">
                    {t("Newsfeed.Article")}
                  </span>
                </span>
              ) : null}
              <PostInteractionModal
                postId={item?.postInfo?.postId}
                AllPostList={AllPostList}
                postList={postList}
                setPage={setPage}
                setPostList={setPostList}
                page={page}
                setTotalPages={setTotalPages}
                totalPages={totalPages}
                item={item}
                restrictedViewerToken={restrictedViewerToken}
                PostOriginType={"User"}
                postOriginId={userUid}
                isfollowed={isfollowed}
                setIsfollowed={setIsfollowed}
                isfollowedComp={isfollowedComp}
                setIsfollowedComp={setIsfollowedComp}
                setIsConnected={setIsConnected}
                isConnected={isConnected}
                setShowDeletePost={setShowDeletePost}
                showDeletePost={showDeletePost}
                ispinned={resolveFeedPostPinnedState(item)}
                onBeforePostDelete={(deletedPostId) => {
                  if (visibleVideoRef?.current?.videoEl) {
                    try {
                      visibleVideoRef.current.videoEl.pause();
                    } catch (error) {}
                    visibleVideoRef.current = null;
                  }
                  setActiveVideoId(null);
                  if (String(singlepostId) === String(deletedPostId)) {
                    setIsModalOpen(false);
                    setIspostmodalOpen(false);
                  }
                }}
              />
            </div>
          )}
        </div>

        {isArticle ? (
          <h4 className="f-14 fw-bold mb-0">
            {item?.postInfo?.articleInfo?.title}
          </h4>
        ) : (
          <PostComponent
            item={item}
            restrictedViewerToken={restrictedViewerToken}
          />
        )}

        <div
          className={`feed-post${
            item?.postInfo?.postMediaCount === 0 ? " feed-post--no-media" : ""
          }`}
          style={{
            paddingTop: item?.postInfo?.postMediaCount === 0 ? "0px" : "4px",
          }}
        >
          <div className="row">
            {mediaList.slice(0, 4).map((file, idx) => (
              <div
                key={`${item.postInfo.postId}-media-${idx}`}
                className={`col-${mediaCount === 1 ? "12" : "6"} ${
                  mediaCount === 3 && idx === 0 ? "col-12" : ""
                }`}
                onClick={() => handleMediaClick(item, idx)}
                style={{
                  position: "relative",
                  cursor: "pointer",
                  padding: getMediaCellPadding(mediaCount, idx),
                }}
              >
                {!isVideo ? (
                  <ImageWithLoader
                    src={file?.mediaName}
                    alt=""
                    className="img-fluid m-auto modal-imag"
                    width={"100%"}
                    onError={handlePostMediaImageError}
                  />
                ) : (
                  <div className="newsfeed-video">
                    <PostVideoCommon
                      file={file}
                      item={item}
                      restrictedViewerToken={restrictedViewerToken}
                      setActiveVideoId={setActiveVideoId}
                      activeVideoId={activeVideoId}
                      AllPostList={AllPostList}
                      setPostList={setPostList}
                      visibleVideoRef={visibleVideoRef}
                    />
                  </div>
                )}
                {idx === 3 && item?.postInfo?.postMediaCount > 4 ? (
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50 text-white">
                    +{item.postInfo.postMediaCount - 4}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          {guestModeData === "true" ? null : isArticle ? (
            <div className="row comment-gradient-article">
              <PostComponent
                item={item}
                restrictedViewerToken={restrictedViewerToken}
              />
              <div className="addComment">
                <div
                  className="postCommentWrapper"
                  onClick={(event) => event.stopPropagation()}
                >
                  <PostComment
                    item={item}
                    postId={item?.postInfo?.postId}
                    AllPostList={AllPostList}
                    postList={postList}
                    setPage={setPage}
                    setPostList={setPostList}
                    page={page}
                    setTotalPages={setTotalPages}
                    totalPages={totalPages}
                    updateCommentCount={updateCommentCount}
                    ispostmodalOpen={ispostmodalOpen}
                    setIspostmodalOpen={setIspostmodalOpen}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="row comment-gradient">
              <div className="addComment">
                <div
                  className="postCommentWrapper"
                  onClick={(event) => event.stopPropagation()}
                >
                  <PostComment
                    item={item}
                    postId={item?.postInfo?.postId}
                    AllPostList={AllPostList}
                    postList={postList}
                    setPage={setPage}
                    setPostList={setPostList}
                    page={page}
                    setTotalPages={setTotalPages}
                    totalPages={totalPages}
                    updateCommentCount={updateCommentCount}
                    ispostmodalOpen={ispostmodalOpen}
                    setIspostmodalOpen={setIspostmodalOpen}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          className={`postData d-flex align-items-center justify-content-between ${
            item?.postInfo?.likeCount === 0 &&
            item?.postInfo?.commentCount === 0 &&
            item?.postInfo?.shareCount === 0
              ? ""
              : "border-custom1"
          }`}
        >
          <div className="ms-2">
            {item?.postInfo?.likeCount ? (
              <p
                className="f-10 like-details"
                onClick={() => handleShowLikes(item)}
              >
                {item.postInfo.likeCount + dLike}{" "}
                <ThumbUpOutlinedIcon
                  style={{ color: "#00419f", fontSize: "13px" }}
                />
              </p>
            ) : null}
          </div>
          <div className="ms-auto d-flex">
            {item?.postInfo?.commentCount ? (
              <p
                className="f-10 me-2 pointer"
                onClick={() => {
                  const imageData = mediaList.map((file) => file.mediaName);
                  openModal(imageData, 0);
                  setSinglePostContent(item);
                  setSinglePostId(item?.postInfo?.postId);
                }}
                style={{ color: "#00419f" }}
              >
                {item.postInfo.commentCount}{" "}
                <ChatOutlinedIcon
                  style={{ color: "#00419f", fontSize: "13px" }}
                />
              </p>
            ) : null}
            <p className="f-10">
              {item?.postInfo?.shareCount
                ? `&  ${item.postInfo.shareCount}`
                : ""}
              {item?.postInfo?.shareCount ? (
                <ReplyAllOutlinedIcon
                  className=" ms-1"
                  style={{ color: "#00419f", fontSize: "13px" }}
                />
              ) : null}
            </p>
          </div>
        </div>

        {guestModeData === "true" ? null : (
          <>
            <div className="postengagement">
              <PostEngagement
                item={item}
                setIsLiked={(value) => {
                  setPostLikeState((prev) => ({
                    ...prev,
                    [String(item?.postInfo?.postId)]: Boolean(value),
                  }));
                }}
                isLiked={Boolean(
                  postLikeState[String(item?.postInfo?.postId)] ??
                    item?.postInfo?.postActionsInfo?.isLiked
                )}
                postId={item?.postInfo?.postId}
                AllPostList={AllPostList}
                postList={postList}
                setPage={setPage}
                setPostList={setPostList}
                page={page}
                setTotalPages={setTotalPages}
                totalPages={totalPages}
                updateLikeCount={updateLikeCount}
                updateShareCount={updateShareCount}
                openModal={openModal}
                setSinglePostContent={setSinglePostContent}
                setSinglePostId={setSinglePostId}
                ispostmodalOpen={ispostmodalOpen}
                setIspostmodalOpen={setIspostmodalOpen}
                emojiList={emojiList}
                updatePostLike={updatePostLike}
                setImageArray={setImageArray}
                setInitialSlideIndex={setInitialSlideIndex}
              />
            </div>
            <Comment
              key={item?.postInfo?.postId}
              item={item}
              isOwnedPost={item?.postInfo?.isOwned}
              postId={item?.postInfo?.postId}
              AllPostList={AllPostList}
              postList={postList}
              setPage={setPage}
              setPostList={setPostList}
              page={page}
              setTotalPages={setTotalPages}
              totalPages={totalPages}
              emojiList={emojiList}
              setSinglePostContent={setSinglePostContent}
              openModal={openModal}
              setImageArray={setImageArray}
              setInitialSlideIndex={setInitialSlideIndex}
              setSinglePostId={setSinglePostId}
              updateLikeCount={updateLikeCount}
              updateCommentLike={updateCommentLike}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="newsfeedPosts search-result-list sr-newsfeed-list">
        {posts.map((item, index) => {
          if (item?.feedItem?.postInfo?.postId) {
            return renderFeedCard(item.feedItem);
          }
          if (item?.enriched || item?.isDummy) {
            return renderFallback ? renderFallback(item, index) : null;
          }
          return (
            <div key={`search-post-skeleton-${item?.id || index}`}>
              <PostFeedSkeleton count={1} />
            </div>
          );
        })}
      </div>

      {isModalOpen ? (
        <ImageSliderModal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          images={modalImages}
          isLoading={modalLoading}
          setIsLoading={setModalLoading}
          item={singlePostContent}
          initialPostItem={singlePostContent}
          singlepostId={singlepostId}
          setSinglePostId={setSinglePostId}
          updateShareCount={updateShareCount}
          updateCommentLike={updateCommentLike}
          updateLikeCount={updateLikeCount}
          updatePostLike={updatePostLike}
          totalPages={totalPages}
          setTotalPages={setTotalPages}
          page={page}
          setPostList={setPostList}
          setPage={setPage}
          postList={postList}
          AllPostList={AllPostList}
          PostEngagement={PostEngagement}
          dLike={dLike}
          updateCommentCount={updateCommentCount}
          ispostmodalOpen={ispostmodalOpen}
          setIspostmodalOpen={setIspostmodalOpen}
          openModal={openModal}
          setSinglePostContent={setSinglePostContent}
          initialSlideIndex={initialSlideIndex}
          setInitialSlideIndex={setInitialSlideIndex}
          imageArray={imageArray}
          isLiked={modalIsLiked}
          setIsLiked={handleModalSetIsLiked}
          restrictedViewerToken={restrictedViewerToken}
          updateReplyCommentLike={updateReplyCommentLike}
          isfollowed={isfollowed}
          setIsfollowed={setIsfollowed}
          isfollowedComp={isfollowedComp}
          setIsfollowedComp={setIsfollowedComp}
        />
      ) : null}

      <LikesDetails
        showModal={showLikesModal}
        handleLikesCloseModal={() => setShowLikesModal(false)}
        postId={likesPostId}
        postLikedType={postLikedType}
        setShowModal={setShowLikesModal}
      />
    </>
  );
};

export default SearchNewsFeedPosts;
